import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { LogoLockup, LogoMark } from "@/components/Logo";
import { CalendarDays, Clock, Check, HelpCircle, X, Printer, Users, Music, ChevronDown, Youtube, ExternalLink } from "lucide-react";

function fmtDay(d) {
  try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }); }
  catch (e) { return d; }
}
function fmtShort(d) {
  try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch (e) { return d; }
}
const statusIcon = (s) => s === "confirmed" ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : s === "declined" ? <X className="w-3.5 h-3.5 text-rose-400" /> : <HelpCircle className="w-3.5 h-3.5 text-amber-400" />;

export default function PublicRoster() {
  const { shareId } = useParams();
  const [data, setData] = useState(undefined);
  const [openSong, setOpenSong] = useState(null); // `${svcId}-${idx}`

  useEffect(() => {
    axios.get(`${API}/public/roster/${shareId}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [shareId]);

  if (data === undefined) return <div className="wf-app-bg min-h-screen flex items-center justify-center"><LogoMark size={48} /></div>;
  if (data === null) return (
    <div className="wf-app-bg min-h-screen flex flex-col items-center justify-center p-6 text-center">
      <LogoMark size={56} />
      <h1 className="font-display text-2xl font-bold text-[#F7F5F0] mt-4">Roster unavailable</h1>
      <p className="text-[#8A97AC] mt-2">This link may have been turned off. Please ask your worship leader for an updated link.</p>
    </div>
  );

  const { title, start, end, church, services, include_charts } = data;
  // group by date
  const byDate = {};
  services.forEach((s) => { (byDate[s.date] = byDate[s.date] || []).push(s); });
  const dates = Object.keys(byDate).sort();

  return (
    <div className="wf-app-bg min-h-screen pb-16">
      <style>{`@media print { .no-print { display:none !important; } .wf-app-bg { background:#fff !important; } * { color:#111 !important; } .wf-card, .print-card { background:#fff !important; border-color:#ddd !important; } } @page { margin: 14mm; }`}</style>
      <header className="no-print wf-glass border-b border-[#E5C378]/12 px-4 py-4 flex items-center justify-between sticky top-0 z-20">
        <LogoLockup subtitle={church?.toUpperCase() || "WORSHIP FLOW"} />
        <button onClick={() => window.print()} data-testid="print-roster" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg wf-gold-btn font-semibold text-sm"><Printer className="w-4 h-4" /> Print / Save PDF</button>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-gradient-to-br from-[#121B2F] to-[#0A0F1D] border border-[#E5C378]/25 rounded-2xl p-6 mb-6 relative overflow-hidden print-card">
          <div className="absolute top-0 right-0 w-40 h-40 bg-[#E5C378]/10 rounded-full blur-3xl" />
          <div className="eyebrow mb-1">Worship Roster</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-white" data-testid="roster-title">{title}</h1>
          <div className="flex flex-wrap gap-4 text-sm text-[#A0AEC0] mt-3">
            <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> {fmtShort(start)} — {fmtShort(end)}</span>
            <span className="flex items-center gap-1.5"><Users className="w-4 h-4" /> {services.length} service{services.length === 1 ? "" : "s"}</span>
          </div>
        </div>

        {dates.length === 0 && <p className="text-center text-[#8A97AC]">No services scheduled in this range yet.</p>}

        <div className="space-y-6">
          {dates.map((date) => (
            <div key={date} data-testid={`roster-day-${date}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="h-px flex-1 bg-[#E5C378]/20" />
                <span className="text-sm font-display font-semibold text-[#E5C378] uppercase tracking-wide">{fmtDay(date)}</span>
                <div className="h-px flex-1 bg-[#E5C378]/20" />
              </div>

              {byDate[date].map((s) => (
                <div key={s.id} className="wf-card print-card rounded-2xl p-5 mb-3">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <div>
                      <h3 className="font-display font-semibold text-[#F7F5F0]">{s.title}</h3>
                      <div className="text-xs text-[#8A97AC]">{s.type}</div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-sm text-[#B8C1D1]"><Clock className="w-4 h-4 text-[#E5C378]" /> {s.time}</span>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <div className="eyebrow mb-1.5 flex items-center gap-1"><Users className="w-3 h-3" /> Team</div>
                      {s.team.length === 0 ? <p className="text-xs text-[#6E7D97]">Not finalised yet.</p> : (
                        <div className="space-y-1">
                          {s.team.map((a, i) => (
                            <div key={i} className="flex items-center justify-between text-sm">
                              <span className="text-[#F7F5F0]">{a.name} <span className="text-[#8A97AC] text-xs">· {a.slot}</span></span>
                              {statusIcon(a.status)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="eyebrow mb-1.5 flex items-center gap-1"><Music className="w-3 h-3" /> Song order</div>
                      {s.songs.length === 0 ? <p className="text-xs text-[#6E7D97]">No songs yet.</p> : (
                        <div className="space-y-1">
                          {s.songs.map((song, i) => {
                            const hasChart = include_charts && ((song.sections && song.sections.length) || song.lyrics || song.links?.youtube || song.links?.spotify);
                            const key = `${s.id}-${i}`;
                            return (
                              <div key={i} className="text-sm">
                                <button
                                  onClick={() => hasChart && setOpenSong(openSong === key ? null : key)}
                                  data-testid={`roster-song-${s.id}-${i}`}
                                  className={`w-full flex items-center justify-between gap-2 text-left ${hasChart ? "cursor-pointer" : "cursor-default"}`}>
                                  <span className="text-[#F7F5F0] min-w-0 truncate"><span className="text-[#6E7D97] font-mono text-xs mr-1">{i + 1}.</span>{song.title}</span>
                                  <span className="flex items-center gap-1 shrink-0">
                                    {song.key && <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-[#E5C378]/15 text-[#E5C378] border border-[#E5C378]/30">{song.key}</span>}
                                    {song.bpm ? <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-sky-500/15 text-sky-300 border border-sky-500/30">{song.bpm}</span> : null}
                                    {hasChart && <ChevronDown className={`w-3.5 h-3.5 text-[#6E7D97] transition-transform ${openSong === key ? "rotate-180" : ""}`} />}
                                  </span>
                                </button>
                                {hasChart && openSong === key && (
                                  <div className="mt-2 mb-2 border-t border-white/5 pt-2">
                                    <div className="flex gap-3 mb-2">
                                      {song.links?.youtube && <a href={song.links.youtube} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#8A97AC] hover:text-[#E5C378]"><Youtube className="w-3.5 h-3.5" /> YouTube</a>}
                                      {song.links?.spotify && <a href={song.links.spotify} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#8A97AC] hover:text-[#E5C378]"><ExternalLink className="w-3.5 h-3.5" /> Spotify</a>}
                                    </div>
                                    {song.sections?.length ? (
                                      <div className="font-mono text-xs">
                                        {song.sections.map((sec, si) => (
                                          <div key={si} className="mb-2">
                                            <div className="text-[#7EA293] uppercase tracking-wider text-[10px] font-bold mb-0.5">{sec.name}</div>
                                            {sec.lines.map((ln, li) => (
                                              <div key={li}>
                                                {ln.chords?.trim() && <pre className="text-[#FFD56B] font-bold m-0 whitespace-pre-wrap">{ln.chords}</pre>}
                                                {ln.lyrics?.trim() && <div className="text-[#E7EAF0]">{ln.lyrics}</div>}
                                              </div>
                                            ))}
                                          </div>
                                        ))}
                                      </div>
                                    ) : song.lyrics ? <div className="whitespace-pre-wrap text-xs text-[#E7EAF0]">{song.lyrics}</div> : null}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        <p className="text-center text-[10px] text-[#6E7D97] italic mt-8">"At the right time, I, the LORD, will make it happen." — Isaiah 60:22 · Powered by Worship Flow</p>
      </div>
    </div>
  );
}
